import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch, useAppSelector, useModal } from '@/shared/hooks';
import { Avatar } from '@/shared/ui';

import { DoctorsAvatars } from '@/entities/doctors';
import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';

import { patientDoctorsModel } from '@/features/patientDoctors';
import {
  RemoveDoctorModal,
  useRemoveDoctorModal,
} from '@/features/removeDoctor';
import { ShowAllTreatingDoctorsModal } from '@/features/showAllTreatingDoctors';
import { AddDoctorsModal } from '@/features/treatingDoctors';

import { MAX_ROWS_COUNT } from '../../config';
import { getShortedList } from '../../lib';
import { PeopleCard } from '../PeopleCard/PeopleCard';

type TreatingDoctorsInlineProps = {
  patientID: string;
  className?: string;
};

export const TreatingDoctorsInline: FC<TreatingDoctorsInlineProps> = (
  props,
) => {
  const { patientID, className } = props;

  const dispatch = useAppDispatch();
  const treatingDoctors = useAppSelector(
    patientDoctorsModel.selectors.selectPatientTreatingDoctors(patientID),
  );
  const { CanViewTeam, CanEditTeam } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const {
    openRemoveDoctorModal,
    closeRemoveDoctorModal,
    isRemoveDoctorModalOpen,
    currentDoctorID,
  } = useRemoveDoctorModal();

  const {
    isOpen: isShowAllModalOpen,
    openModal: openShowAllModal,
    closeModal: closeShowAllModal,
  } = useModal();

  if (!CanViewTeam || !treatingDoctors) {
    return null;
  }

  const openAddDoctorsModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.TreatingDoctorsModal,
        data: {
          patientID,
          currentDoctorIDs: treatingDoctors.map((doctor) => doctor.ID),
        },
      }),
    );
  };

  const doctorsCount = treatingDoctors.length;
  const visibleDoctors = getShortedList(treatingDoctors);
  const hiddenCount = doctorsCount - MAX_ROWS_COUNT;

  return (
    <>
      <PeopleCard
        className={className}
        dataSection="treating-doctors"
        label={
          <FormattedMessage
            id="patientInfo.treatingDoctors"
            defaultMessage="Treating doctors"
          />
        }
        onOpen={doctorsCount > 0 ? openShowAllModal : undefined}
        action={
          CanEditTeam && (
            <PeopleCard.ActionButton onClick={openAddDoctorsModal}>
              {'+ '}
              <FormattedMessage id="global.add" defaultMessage="Add" />
            </PeopleCard.ActionButton>
          )
        }
      >
        {doctorsCount === 0 && (
          <PeopleCard.Muted>
            <FormattedMessage
              id="patientInfo.noTreatingDoctors"
              defaultMessage="Not assigned"
            />
          </PeopleCard.Muted>
        )}

        {doctorsCount === 1 && (
          <DoctorsAvatars
            doctor={treatingDoctors[0]}
            isOnlyOneDoctor
            size={32}
            disabled={treatingDoctors[0].Status.case !== 'Active'}
          />
        )}

        {doctorsCount > 1 && (
          <>
            <Avatar.Group>
              {visibleDoctors.map((doctor) => (
                <DoctorsAvatars
                  key={doctor.ID}
                  doctor={doctor}
                  isOnlyOneDoctor={false}
                  size={32}
                  disabled={doctor.Status.case !== 'Active'}
                />
              ))}
              {hiddenCount > 0 && <Avatar size={32} count={hiddenCount} />}
            </Avatar.Group>
            <PeopleCard.Count>{doctorsCount}</PeopleCard.Count>
          </>
        )}
      </PeopleCard>

      <RemoveDoctorModal
        patientID={patientID}
        staffID={currentDoctorID}
        isOpen={isRemoveDoctorModalOpen}
        onCancel={closeRemoveDoctorModal}
      />

      <AddDoctorsModal />

      <ShowAllTreatingDoctorsModal
        isOpen={isShowAllModalOpen}
        onClose={closeShowAllModal}
        treatingDoctors={treatingDoctors}
        openRemoveDoctorModal={openRemoveDoctorModal}
      />
    </>
  );
};
